import { Button } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { WizardQuestions_QuestionFragment } from "../../documents/WizardQuestions.generated.js";
import { WizardRendererRef } from "./WizardRendererProps.js";

interface Props {
  question: WizardQuestions_QuestionFragment | null;
  /** Nothing is selected and nothing is loading: the mode has nothing left. */
  isDone: boolean;
  /** The mode to move on to from the done state, where one is left. */
  nextMode: { label: string; onSelect: () => void } | null;
  onNext: () => void;
  onPrevious: () => void;
  canGoNext: boolean;
  canGoPrevious: boolean;
  busy: boolean;
  /** Set by a renderer that stages its answer; forward submits it. */
  stagedRef: React.RefObject<WizardRendererRef>;
}

/**
 * The wizard's two moves, drawn in the page's sticky action bar so they stay
 * on screen however tall the question is. The bar centres its content as one
 * group, so back and forward sit adjacent and in reading order.
 */
export const WizardQuestionNavigation = ({
  question,
  isDone,
  nextMode,
  onNext,
  onPrevious,
  canGoNext,
  canGoPrevious,
  busy,
  stagedRef,
}: Props) => {
  const { t } = useTranslate(["Global", "Quote"]);

  const forwardSubmits =
    (question?.__typename === "WizardDecisionQuestion" &&
      question.behavior === "many") ||
    question?.__typename === "WizardMultiplePropertiesQuestion" ||
    question?.__typename === "WizardMultipleRecommendationsQuestion";

  return (
    <>
      <Button
        variant="outlined"
        disabled={!canGoPrevious || busy}
        onClick={onPrevious}
      >
        {t("Back", { ns: "Global" })}
      </Button>
      {isDone && nextMode ? (
        // Forward has nowhere left to go here, so the slot carries the one
        // move that does: on to the next mode's questions.
        <Button variant="contained" disabled={busy} onClick={nextMode.onSelect}>
          {t("Continue with {mode}", { ns: "Quote", mode: nextMode.label })}
        </Button>
      ) : (
        <Button
          variant={forwardSubmits ? "contained" : "outlined"}
          disabled={(!forwardSubmits && !canGoNext) || busy}
          onClick={forwardSubmits ? () => stagedRef.current?.submit() : onNext}
        >
          {t("Next", { ns: "Global" })}
        </Button>
      )}
    </>
  );
};
